import type { MouseEvent } from 'react';
import { useGT } from 'gt-react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { ProductCell } from './ProductCell';
import type { NeedGroup } from '../utils/needRows';

export function NeedProductCell({
  need,
  line,
  expanded,
  onToggle,
}: {
  need: NeedGroup;
  line: AssignmentLine;
  expanded: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  const label = expanded
    ? gt('Hide {count} lines for {item}', {
        count: need.lines.length,
        item: line.productSku ?? line.productName,
        context: 'buyer board need row collapse button',
      })
    : gt('Show {count} lines for {item}', {
        count: need.lines.length,
        item: line.productSku ?? line.productName,
        context: 'buyer board need row expand button',
      });
  const hasStock = need.lines.length > need.salesOrderCount;
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    onToggle();
  };
  // The SO count is the expand toggle; the visual treatment is still open.
  return (
    <span className="flex min-w-0 items-center gap-1">
      <ProductCell line={line} />
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={label}
        title={label}
        onClick={handleClick}
        className="shrink-0 cursor-pointer rounded px-0.5 text-xs tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {hasStock
          ? gt('{count} SOs + stock', {
              count: need.salesOrderCount,
              context:
                'buyer board need row: its sales order lines and the stock line ordered with them',
            })
          : gt('{count} SOs', {
              count: need.salesOrderCount,
              context: 'buyer board need row, count of sales order lines',
            })}
      </button>
    </span>
  );
}
